/**
 * EmployeeLeavesTab — Permisos y licencias de UN trabajador, en la ficha.
 *
 * Existe porque el modal del trabajador no tenía dónde ver esto: las vacaciones
 * tienen su pestaña desde el #1175/#1176 y los permisos quedaron afuera, así que
 * la única forma de revisarlos era el listado general del tenant.
 *
 * Es de SOLO LECTURA a propósito. Aprobar y rechazar vive en la pestaña de
 * Vacaciones, que es donde llega el enlace del correo de aviso y donde se resuelve
 * la cola de pendientes. Duplicar la acción acá abriría dos caminos para lo mismo
 * y el estado de uno no se vería reflejado en el otro sin recargar.
 */
import { useMemo } from 'react'
import { Clock, FileText } from 'lucide-react'
import { useLeaveRequests } from '../hooks/usePayroll'
import { formatDate } from '@/lib/locale'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { DataTable } from '../../../components/ds/DataTable'
import { LEAVE_REQUEST_STATUS } from '../../../components/ds/statusCatalogs'
import { useBeneficiosDeLaFicha } from '../hooks/useBeneficios'
import { SaldosDeBeneficios } from './SaldosDeBeneficios'


/** Los días tal como se pidieron: `0.5` se muestra «0,5», y `1.0` se muestra «1». */
function formatDias(v: unknown, locale: string) {
  const n = parseFloat(String(v ?? 0))
  if (!isFinite(n)) return '—'
  return n.toLocaleString(locale, { maximumFractionDigits: 1 })
}

interface Props {
  employeeId: string
  locale: string
}

export default function EmployeeLeavesTab({ employeeId, locale }: Props) {
  const { data, isLoading, error } = useLeaveRequests(
    { employee_id: employeeId }, { enabled: !!employeeId })

  const leaves = data?.leaves ?? []
  // Saldo de los beneficios de días (#1765). Si falla (p. ej. sin permiso) no se
  // muestra: la tabla de permisos sigue sirviendo sola.
  const { data: beneficios } = useBeneficiosDeLaFicha(employeeId)

  // Los pendientes primero — es lo que requiere una acción — y dentro de cada
  // grupo lo más reciente arriba.
  const ordenados = useMemo(() => {
    const peso = (s: string) => (s === 'pending' || s === 'pending_second' ? 0 : 1)
    return [...leaves].sort((a: any, b: any) =>
      peso(a.status) - peso(b.status) ||
      String(b.start_date).localeCompare(String(a.start_date)))
  }, [leaves])

  const totales = useMemo(() => {
    const aprobados = leaves.filter((l: any) => l.status === 'approved')
    const dias = aprobados.reduce(
      (s: number, l: any) => s + (parseFloat(String(l.days ?? 0)) || 0), 0)
    return {
      aprobados: aprobados.length,
      pendientes: leaves.filter((l: any) => l.status === 'pending' || l.status === 'pending_second').length,
      dias,
    }
  }, [leaves])

  if (isLoading) {
    return <p className="text-sm text-content-subtle">Cargando permisos…</p>
  }
  // Un error se dice, no se disfraza de lista vacía: sin esto un 403 se vería
  // igual que «no tiene permisos registrados» (#968).
  if (error) {
    return (
      <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-4 shadow-card">
        <p className="text-sm text-content-muted">
          No se pudieron cargar los permisos. Volver a intentar en unos momentos.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Clock size={15} className="text-content-muted" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-content">Permisos y licencias</h3>
      </div>

      {beneficios && (
        <SaldosDeBeneficios saldos={beneficios.results} sinBeneficio={beneficios.sin_beneficio}
                            locale={locale} />
      )}

      {leaves.length === 0 ? (
        <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-6 text-center shadow-card">
          <p className="text-sm text-content-muted">
            No hay permisos ni licencias registrados.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-3 shadow-card">
              <p className="text-[11px] uppercase tracking-wide text-content-subtle">Aprobados</p>
              <p className="text-xl font-bold text-content">{totales.aprobados}</p>
            </div>
            <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-3 shadow-card">
              <p className="text-[11px] uppercase tracking-wide text-content-subtle">Días aprobados</p>
              <p className="text-xl font-bold text-content">{formatDias(totales.dias, locale)}</p>
            </div>
            <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-3 shadow-card">
              <p className="text-[11px] uppercase tracking-wide text-content-subtle">Pendientes</p>
              <p className={`text-xl font-bold ${totales.pendientes ? 'text-warning-content-soft' : 'text-content'}`}>
                {totales.pendientes}
              </p>
            </div>
          </div>

          {/* Sin paginacion: el endpoint devuelve los permisos del trabajador
              completos, asi que el orden por clic ordena la lista entera y no
              una pagina. El orden inicial —pendientes primero— es el de
              `ordenados` y se conserva mientras nadie toque un encabezado. */}
          <DataTable
            rows={ordenados}
            rowKey={(l: any) => l.id}
            minWidth={760}
            caption="Permisos y licencias del trabajador"
            columns={[
              {
                key: 'leave_type_display', header: 'Tipo', sortable: true,
                render: (l: any) => <span className="text-content">{l.leave_type_display}</span>,
              },
              {
                key: 'start_date', header: 'Desde', sortable: true, width: 110,
                render: (l: any) => (
                  <span className="whitespace-nowrap text-content-muted">
                    {formatDate(l.start_date, locale)}
                  </span>
                ),
              },
              {
                key: 'end_date', header: 'Hasta', sortable: true, width: 110,
                render: (l: any) => (
                  <span className="whitespace-nowrap text-content-muted">
                    {formatDate(l.end_date, locale)}
                  </span>
                ),
              },
              {
                key: 'days', header: 'Días', numeric: true, sortable: true, width: 80,
                // ⚠️ `days` llega como texto: sin esto «10» ordenaria antes que «2».
                sortValue: (l: any) => parseFloat(String(l.days ?? 0)) || 0,
                render: (l: any) => (
                  <span className="font-medium text-content">{formatDias(l.days, locale)}</span>
                ),
              },
              {
                key: 'status', header: 'Estado', sortable: true, width: 130,
                render: (l: any) => (
                  <StatusBadge
                    status={l.status}
                    catalog={LEAVE_REQUEST_STATUS}
                    label={LEAVE_REQUEST_STATUS[l.status] ? undefined : (l.status_display || l.status)}
                  />
                ),
              },
              {
                // El motivo puede ser largo y venir con saltos de linea del
                // portal: se recorta con title para no romper la fila.
                key: 'reason', header: 'Motivo',
                render: (l: any) => (
                  <span className="block max-w-[220px] truncate text-content-muted" title={l.reason || ''}>
                    {l.reason || '—'}
                  </span>
                ),
              },
              {
                key: 'document_url', header: 'Doc.', width: 60,
                render: (l: any) => (
                  <div className="text-center">
                    {l.document_url ? (
                      <a href={l.document_url} target="_blank" rel="noopener noreferrer"
                         className="text-primary hover:underline inline-flex items-center"
                         title="Ver documento adjunto">
                        <FileText size={13} aria-hidden="true" />
                      </a>
                    ) : (
                      <span className="text-content-subtle">—</span>
                    )}
                  </div>
                ),
              },
            ]}
          />

          <p className="text-[11px] text-content-subtle leading-snug">
            Los permisos no descuentan días del saldo de vacaciones; los beneficios de días
            descuentan de su propio saldo. Para aprobar o rechazar, ir a Nómina → Vacaciones.
          </p>
        </>
      )}
    </div>
  )
}
